import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, Upload } from "lucide-react";
import { toast } from "sonner";
import PadFirma from "../components/PadFirma";
import { API_BASE } from "../utils/api";
import { formatCurrency, formatNumber } from "../utils/formatters";

// Pagina PUBBLICA, raggiunta dal cliente senza login: niente Sidebar/MainLayout,
// niente apiFetch (aggiungerebbe l'Authorization di un eventuale operatore già
// loggato su questo stesso browser, e su 401 gli cancellerebbe la sessione).
// Tutte le chiamate vanno dirette su API_BASE con il token nell'URL.
//
// Design: handoff dedicato "Firma Contratto - Step" (flusso a 3 passi: Rivedi
// → Clausole → Firma). Font e colori sono un sistema a sé (Poppins,
// .firma-font-ui in index.css), non il font/i token di prodotto — stessa
// convenzione già in uso per il wizard interno "Contratti" (.contratto-font-ui).

const COLOR = {
  pageBg: "#E9ECF6",
  cardBg: "#F5F6FB",
  surface: "#FFFFFF",
  headerBg: "#1C1F2E",
  progressInactive: "#3A3D4D",
  textPrimary: "#1C1F2E",
  textSecondary: "#6E7590",
  headerTextSecondary: "#9AA0B5",
  headerTextStep: "#C9CDDD",
  labelUpper: "#8A90A6",
  padPlaceholder: "#AEB3C6",
  borderOuter: "#DEE2EF",
  borderInner: "#E4E7F2",
  divider: "#EDEFF7",
  borderInput: "#D7DAEA",
  borderHover: "#C6CBDF",
  accent: "#5B55E0",
  accentHover: "#4A44D4",
  focusRing: "#EEEDFC",
  disabledSurface: "#EFF0F7",
  disabledText: "#9AA0B5",
  positiveText: "#12875F",
  positiveSurface: "#E6F6EF",
};

const STEP_LABELS = ["Rivedi", "Clausole", "Firma"];

const FIRMA_UPLOAD_MIME = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
const FIRMA_UPLOAD_MAX_BYTES = 15 * 1024 * 1024;

function formattaData(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT");
  } catch (e) {
    return "—";
  }
}

function formattaDataOra(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT");
  } catch (e) {
    return "—";
  }
}

// Combina attivazione una tantum + eventuali addon ricorrenti mensili
// (importo fisso e/o fee %) nelle due celle "Attivazione"/"Ricorrente" del
// riepilogo — stessa logica di _righe_corrispettivo in contratti_pdf.py,
// qui solo per il riepilogo compatto (il dettaglio riga per riga resta nel PDF).
function derivaEconomia(servizio) {
  const addons = servizio?.addons || [];
  let attivazione = Number(servizio?.importo_attivazione || 0);
  const extraUnaTantum = [];
  for (const a of addons) {
    if (a.ricorrenza !== "una_tantum") continue;
    if (a.tipo_valore === "percentuale") extraUnaTantum.push(`${formatNumber(a.valore)}% ${a.label}`);
    else attivazione += Number(a.valore || 0);
  }

  const mensiliImporto = addons.filter((a) => a.ricorrenza === "mensile" && a.tipo_valore === "importo");
  const mensiliPercentuale = addons.filter((a) => a.ricorrenza === "mensile" && a.tipo_valore === "percentuale");
  const totaleMensile = mensiliImporto.reduce((s, a) => s + Number(a.valore || 0), 0);
  const feeParti = mensiliPercentuale.map((a) => `fee ${formatNumber(a.valore)}%`);

  let ricorrenteNota;
  if (totaleMensile > 0 && feeParti.length) ricorrenteNota = `+ IVA / mese, ${feeParti.join(", ")}`;
  else if (totaleMensile > 0) ricorrenteNota = "+ IVA / mese";
  else if (feeParti.length) ricorrenteNota = feeParti.join(", ");
  else ricorrenteNota = "Nessun costo ricorrente";

  return {
    attivazione,
    attivazioneNota: extraUnaTantum.length ? `+ IVA una tantum (${extraUnaTantum.join(", ")})` : "+ IVA una tantum",
    ricorrente: totaleMensile,
    ricorrenteHaImporto: totaleMensile > 0,
    ricorrenteNota,
  };
}

// ==================== Sotto-componenti di stile (design tokens verbatim) ====================

function BottonePrimario({ children, onClick, disabled, style = {}, testId }) {
  const [hover, setHover] = useState(false);
  if (disabled) {
    return (
      <div
        style={{
          flex: 1, padding: 16, boxSizing: "border-box", borderRadius: 12,
          background: COLOR.disabledSurface, color: COLOR.disabledText,
          fontSize: 15, fontWeight: 600, textAlign: "center", ...style,
        }}
        data-testid={testId}
      >
        {children}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        flex: 1, padding: 16, border: 0, borderRadius: 12, cursor: "pointer",
        background: hover ? COLOR.accentHover : COLOR.accent, color: "#fff",
        fontSize: 15, fontWeight: 600,
        boxShadow: "0 10px 24px -12px rgba(91,85,224,.9)",
        ...style,
      }}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function BottoneSecondario({ children, onClick, testId }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: "16px 22px", border: `1px solid ${hover ? "#9AA0B5" : COLOR.borderInput}`, borderRadius: 12,
        background: "#fff", fontSize: 15, fontWeight: 600, color: "#3D4358", cursor: "pointer",
      }}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

export default function FirmaContrattoPage() {
  const { token } = useParams();
  const [fase, setFase] = useState("caricamento"); // caricamento | non_valido | scaduto | errore | flusso
  const [dati, setDati] = useState(null);
  const [erroreMsg, setErroreMsg] = useState("");

  const [firmato, setFirmato] = useState(false);
  const [esitoFirma, setEsitoFirma] = useState(null);
  const [step, setStep] = useState(1);

  const [nome, setNome] = useState("");
  const [accettaClausole, setAccettaClausole] = useState(false);
  const [accettaPrivacy, setAccettaPrivacy] = useState(false);
  const [haFirmaDisegnata, setHaFirmaDisegnata] = useState(false);
  const [inviandoFirma, setInviandoFirma] = useState(false);
  const padRef = useRef(null);

  const [mostraUploadCarta, setMostraUploadCarta] = useState(false);
  const [fileCaricato, setFileCaricato] = useState(null);
  const [caricandoUpload, setCaricandoUpload] = useState(false);

  const caricaDati = async () => {
    setFase("caricamento");
    try {
      const res = await fetch(`${API_BASE}/firma/${token}`);
      if (res.status === 404) { setFase("non_valido"); return; }
      if (res.status === 410) { setFase("scaduto"); return; }
      if (res.status === 429) {
        setErroreMsg("Troppi tentativi. Riprova tra qualche minuto.");
        setFase("errore");
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setErroreMsg(err.detail || "Errore nel caricamento del contratto");
        setFase("errore");
        return;
      }
      const json = await res.json();
      setDati(json);
      if (json.stato === "firmato") {
        setEsitoFirma({ firmato_at: json.firmato_at, modalita: json.modalita, copia_inviata_a: json.copia_inviata_a });
        setFirmato(true);
      } else {
        const nomeSuggerito = json.legale_rappresentante
          ? `${json.legale_rappresentante.nome || ""} ${json.legale_rappresentante.cognome || ""}`.trim()
          : "";
        setNome(nomeSuggerito);
        setFirmato(false);
        setStep(1);
      }
      setFase("flusso");
    } catch (e) {
      setErroreMsg("Errore di rete: controlla la connessione e riprova.");
      setFase("errore");
    }
  };

  useEffect(() => {
    caricaDati();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const inviaFirmaTouch = async () => {
    const png = padRef.current?.esportaPng();
    if (!png) {
      toast.error("Disegna la firma nel riquadro prima di continuare");
      return;
    }
    if (nome.trim().length < 3) {
      toast.error("Indica nome e cognome di chi firma");
      return;
    }
    setInviandoFirma(true);
    try {
      const res = await fetch(`${API_BASE}/firma/${token}/firma-touch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firma_png_base64: png,
          nome_firmatario: nome.trim(),
          accetta_clausole: accettaClausole,
          accetta_privacy: accettaPrivacy,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        setEsitoFirma(json);
        setFirmato(true);
      } else if (res.status === 409) {
        // Doppio tap o ricaricamento dopo una firma già andata a buon fine:
        // il backend risponde 409 senza dati aggiuntivi, la firma va comunque
        // trattata come riuscita, non come un errore da far riprovare.
        setEsitoFirma({ firmato_at: null });
        setFirmato(true);
      } else {
        toast.error(json.detail || "Errore durante la firma. Riprova.");
      }
    } catch (e) {
      toast.error("Errore di rete: la firma non è stata inviata, riprova.");
    } finally {
      setInviandoFirma(false);
    }
  };

  const onSelezionaFile = (file) => {
    if (!file) return;
    if (!FIRMA_UPLOAD_MIME.includes(file.type)) {
      toast.error("Formato non supportato: carica un PDF o una foto (JPG/PNG)");
      return;
    }
    if (file.size > FIRMA_UPLOAD_MAX_BYTES) {
      toast.error("Il file supera i 15MB consentiti");
      return;
    }
    setFileCaricato(file);
  };

  const inviaUpload = async () => {
    if (!fileCaricato) {
      toast.error("Seleziona il file del contratto firmato");
      return;
    }
    if (nome.trim().length < 3) {
      toast.error("Indica nome e cognome di chi ha firmato");
      return;
    }
    setCaricandoUpload(true);
    try {
      const form = new FormData();
      form.append("file", fileCaricato);
      form.append("nome_firmatario", nome.trim());
      const res = await fetch(`${API_BASE}/firma/${token}/upload-firmato`, { method: "POST", body: form });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        setEsitoFirma(json);
        setFirmato(true);
      } else if (res.status === 409) {
        setEsitoFirma({ firmato_at: null });
        setFirmato(true);
      } else {
        toast.error(json.detail || "Errore durante il caricamento. Riprova.");
      }
    } catch (e) {
      toast.error("Errore di rete: il file non è stato caricato, riprova.");
    } finally {
      setCaricandoUpload(false);
    }
  };

  const pdfUrl = `${API_BASE}/firma/${token}/pdf`;
  const mostraProgress = fase === "flusso" && !firmato;
  // Con più servizi il riepilogo li somma: attivazioni e addon di tutti in
  // un'unica coppia di celle. Il dettaglio per servizio resta nel PDF.
  const serviziContratto = dati?.servizi || [];
  const economia = serviziContratto.length ? derivaEconomia({
    importo_attivazione: serviziContratto.reduce((t, s) => t + Number(s.importo_attivazione || 0), 0),
    addons: serviziContratto.flatMap((s) => s.addons || []),
  }) : null;
  const nomiServizi = serviziContratto.map((s) => s.nome).filter(Boolean).join(", ");

  return (
    <div
      className="firma-font-ui"
      style={{ minHeight: "100vh", boxSizing: "border-box", background: COLOR.pageBg, padding: "32px 16px", display: "flex", justifyContent: "center" }}
      data-testid="firma-pubblica-page"
    >
      <div style={{ width: "100%", maxWidth: 780 }}>
        <div style={{ background: COLOR.cardBg, border: `1px solid ${COLOR.borderOuter}`, borderRadius: 22, overflow: "hidden", boxShadow: "0 18px 50px -24px rgba(28,31,46,.35)" }}>

          {/* Header persistente */}
          <div style={{ padding: "22px 28px", background: COLOR.headerBg, color: "#fff" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: mostraProgress ? 18 : 0 }}>
              <span style={{ fontSize: 16, fontWeight: 600 }}>diecixcento</span>
              {dati?.scade_at && !firmato && (
                <span style={{ fontSize: 12, color: COLOR.headerTextSecondary }}>Link valido fino al {formattaData(dati.scade_at)}</span>
              )}
            </div>
            {mostraProgress && (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {STEP_LABELS.map((label, i) => {
                  const idx = i + 1;
                  const attivo = step >= idx;
                  return (
                    <div key={label} style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
                      <div style={{ height: 3, borderRadius: 2, background: attivo ? COLOR.accent : COLOR.progressInactive }} />
                      <span style={{ fontSize: 12, fontWeight: 500, color: step === idx ? "#fff" : COLOR.headerTextStep }}>{idx}. {label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Corpo */}
          <div style={{ padding: "28px 24px" }}>

            {fase === "caricamento" && (
              <div style={{ padding: "40px 10px", textAlign: "center" }}>
                <Loader2 className="animate-spin" style={{ width: 24, height: 24, color: COLOR.textSecondary, margin: "0 auto 12px" }} />
                <p style={{ fontSize: 14, color: COLOR.textSecondary, margin: 0 }}>Caricamento del contratto...</p>
              </div>
            )}

            {fase === "non_valido" && (
              <div style={{ padding: "30px 10px", textAlign: "center" }} data-testid="firma-link-non-valido">
                <AlertTriangle style={{ width: 32, height: 32, color: "#B4474B", margin: "0 auto 14px" }} />
                <div style={{ fontSize: 21, fontWeight: 600, color: COLOR.textPrimary, marginBottom: 6 }}>Link non valido</div>
                <div style={{ fontSize: 14, color: COLOR.textSecondary, lineHeight: 1.6, maxWidth: 380, margin: "0 auto" }}>
                  Questo link non è (più) valido. Contatta Diecixcento per riceverne uno nuovo: 3346603768, oppure via PEC a diecixcentosrls@pec.it.
                </div>
              </div>
            )}

            {fase === "scaduto" && (
              <div style={{ padding: "30px 10px", textAlign: "center" }} data-testid="firma-link-scaduto">
                <AlertTriangle style={{ width: 32, height: 32, color: "#B4780F", margin: "0 auto 14px" }} />
                <div style={{ fontSize: 21, fontWeight: 600, color: COLOR.textPrimary, marginBottom: 6 }}>Link scaduto</div>
                <div style={{ fontSize: 14, color: COLOR.textSecondary, lineHeight: 1.6, maxWidth: 380, margin: "0 auto" }}>
                  Questo link di firma è scaduto. Contatta Diecixcento per richiederne uno nuovo: 3346603768, oppure via PEC a diecixcentosrls@pec.it.
                </div>
              </div>
            )}

            {fase === "errore" && (
              <div style={{ padding: "30px 10px", textAlign: "center" }}>
                <AlertTriangle style={{ width: 32, height: 32, color: "#B4474B", margin: "0 auto 14px" }} />
                <div style={{ fontSize: 21, fontWeight: 600, color: COLOR.textPrimary, marginBottom: 6 }}>Si è verificato un errore</div>
                <div style={{ fontSize: 14, color: COLOR.textSecondary, lineHeight: 1.6, maxWidth: 380, margin: "0 auto 20px" }}>{erroreMsg}</div>
                <BottoneSecondario onClick={caricaDati}>Riprova</BottoneSecondario>
              </div>
            )}

            {fase === "flusso" && firmato && (
              <div style={{ padding: "30px 10px", textAlign: "center" }} data-testid="firma-esito-ok">
                <div style={{ width: 46, height: 46, margin: "0 auto 14px", borderRadius: 999, background: COLOR.positiveSurface, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CheckCircle2 style={{ width: 22, height: 22, color: COLOR.positiveText }} strokeWidth={2.4} />
                </div>
                <div style={{ fontSize: 21, fontWeight: 600, color: COLOR.textPrimary, marginBottom: 6 }}>Contratto firmato</div>
                <div style={{ fontSize: 14, color: COLOR.textSecondary, lineHeight: 1.6, maxWidth: 380, margin: "0 auto 20px" }}>
                  {esitoFirma?.copia_inviata_a
                    ? `Abbiamo inviato una copia con marca temporale a ${esitoFirma.copia_inviata_a}.`
                    : "Il contratto è stato firmato con successo."}
                  {esitoFirma?.firmato_at ? ` (${formattaDataOra(esitoFirma.firmato_at)})` : ""}
                </div>
                <a href={`${pdfUrl}?download=1`} target="_blank" rel="noreferrer" style={{ fontSize: 14, fontWeight: 600 }}>
                  Scarica il contratto firmato
                </a>
              </div>
            )}

            {fase === "flusso" && !firmato && step === 1 && dati && (
              <div data-testid="firma-step-1">
                <h1 style={{ margin: "0 0 4px", fontSize: 25, fontWeight: 600, letterSpacing: "-.02em", color: COLOR.textPrimary }}>Rivedi il contratto</h1>
                <p style={{ margin: "0 0 20px", fontSize: 14.5, color: COLOR.textSecondary }}>
                  {serviziContratto.length > 1 ? "Servizi" : "Servizio"} {nomiServizi}, {dati.durata_mesi} {dati.durata_mesi === 1 ? "mese" : "mesi"}, dal {formattaData(dati.data_inizio)} al {formattaData(dati.data_scadenza)}.
                </p>

                <div style={{ background: COLOR.divider, border: `1px solid ${COLOR.borderInner}`, borderRadius: 16, overflow: "hidden", marginBottom: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 1 }} data-testid="firma-riepilogo">
                  <div style={{ padding: "18px 20px", background: "#fff" }}>
                    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".07em", textTransform: "uppercase", color: COLOR.labelUpper, marginBottom: 5 }}>Azienda</div>
                    <div style={{ fontSize: 14.5, fontWeight: 500, color: COLOR.textPrimary }}>{dati.anagrafica?.ragione_sociale}</div>
                    <div style={{ fontSize: 13, color: COLOR.textSecondary }}>
                      {dati.anagrafica?.cf_piva} · {dati.anagrafica?.sede_via}, {dati.anagrafica?.sede_comune} ({dati.anagrafica?.sede_prov})
                    </div>
                  </div>
                  <div style={{ padding: "18px 20px", background: "#fff" }}>
                    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".07em", textTransform: "uppercase", color: COLOR.labelUpper, marginBottom: 5 }}>Legale rappresentante</div>
                    <div style={{ fontSize: 14.5, fontWeight: 500, color: COLOR.textPrimary }}>{dati.legale_rappresentante?.nome} {dati.legale_rappresentante?.cognome}</div>
                  </div>
                  <div style={{ padding: "18px 20px", background: "#fff" }}>
                    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".07em", textTransform: "uppercase", color: COLOR.labelUpper, marginBottom: 5 }}>Attivazione</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: COLOR.textPrimary }}>
                      {formatCurrency(economia.attivazione)} <span style={{ fontSize: 13, fontWeight: 400, color: COLOR.textSecondary }}>{economia.attivazioneNota}</span>
                    </div>
                  </div>
                  <div style={{ padding: "18px 20px", background: "#fff" }}>
                    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".07em", textTransform: "uppercase", color: COLOR.labelUpper, marginBottom: 5 }}>Ricorrente</div>
                    <div style={{ fontSize: economia.ricorrenteHaImporto ? 18 : 14.5, fontWeight: 600, color: COLOR.textPrimary }}>
                      {economia.ricorrenteHaImporto ? formatCurrency(economia.ricorrente) : "—"}{" "}
                      <span style={{ fontSize: 13, fontWeight: 400, color: COLOR.textSecondary }}>{economia.ricorrenteNota}</span>
                    </div>
                  </div>
                </div>

                <div style={{ background: "#fff", border: `1px solid ${COLOR.borderInner}`, borderRadius: 16, padding: "18px 20px", marginBottom: 22, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                  <div style={{ width: 38, height: 48, flex: "none", borderRadius: 5, background: COLOR.cardBg, border: `1px solid ${COLOR.borderOuter}`, display: "flex", alignItems: "flex-end", justifyContent: "center", paddingBottom: 5, fontSize: 10, fontWeight: 600, color: COLOR.labelUpper }}>PDF</div>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLOR.textPrimary }}>Proposta di adesione — {nomiServizi}</div>
                    <div style={{ fontSize: 13, color: COLOR.textSecondary }}>
                      {dati.pdf_pagine ? `${dati.pdf_pagine} pagine, incluse le Condizioni Generali` : "Incluse le Condizioni Generali"}
                    </div>
                  </div>
                  <a href={pdfUrl} target="_blank" rel="noreferrer" style={{ flex: "none", padding: "9px 15px", borderRadius: 9, border: `1px solid ${COLOR.borderInput}`, fontSize: 13, fontWeight: 600, color: "#3D4358" }} data-testid="firma-pdf-apri">
                    Apri
                  </a>
                  <a href={`${pdfUrl}?download=1`} target="_blank" rel="noreferrer" style={{ flex: "none", fontSize: 13, fontWeight: 600 }} data-testid="firma-pdf-scarica">
                    Scarica
                  </a>
                </div>

                <BottonePrimario onClick={() => setStep(2)} testId="firma-step1-avanti">Ho letto il contratto</BottonePrimario>
              </div>
            )}

            {fase === "flusso" && !firmato && step === 2 && dati && (
              <div data-testid="firma-step-2">
                <h1 style={{ margin: "0 0 4px", fontSize: 25, fontWeight: 600, letterSpacing: "-.02em", color: COLOR.textPrimary }}>Approva le clausole</h1>
                <p style={{ margin: "0 0 20px", fontSize: 14.5, color: COLOR.textSecondary }}>Due conferme richieste dalla legge. Sono obbligatorie per procedere.</p>

                <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 22 }}>
                  <RigaConsenso
                    checked={accettaClausole}
                    onChange={setAccettaClausole}
                    titolo="Clausole specifiche (artt. 1341 e 1342 c.c.)"
                    // Le stesse clausole elencate nel PDF: dipendono dalle condizioni
                    // del servizio (backend/condizioni_generali.py).
                    testo={`Ai sensi degli artt. 1341 e 1342 c.c., dichiaro di approvare specificamente ${dati.clausole_approvate}.`}
                    testId="firma-accetta-clausole"
                  />
                  <RigaConsenso
                    checked={accettaPrivacy}
                    onChange={setAccettaPrivacy}
                    titolo="Trattamento dei dati personali"
                    testo="Acconsento al trattamento dei dati personali ai sensi del Reg. UE 2016/679."
                    testId="firma-accetta-privacy"
                  />
                </div>

                <div style={{ display: "flex", gap: 12 }}>
                  <BottoneSecondario onClick={() => setStep(1)} testId="firma-step2-indietro">Indietro</BottoneSecondario>
                  <BottonePrimario onClick={() => setStep(3)} disabled={!(accettaClausole && accettaPrivacy)} testId="firma-step2-avanti">
                    Vai alla firma
                  </BottonePrimario>
                </div>
              </div>
            )}

            {fase === "flusso" && !firmato && step === 3 && dati && (
              <div data-testid="firma-step-3">
                <h1 style={{ margin: "0 0 4px", fontSize: 25, fontWeight: 600, letterSpacing: "-.02em", color: COLOR.textPrimary }}>Firma</h1>
                <p style={{ margin: "0 0 20px", fontSize: 14.5, color: COLOR.textSecondary }}>
                  Ultimo passo. {dati.anagrafica?.ragione_sociale} — {nomiServizi}, {dati.durata_mesi} {dati.durata_mesi === 1 ? "mese" : "mesi"}.
                </p>

                <label htmlFor="firma-nome" style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#3D4358", marginBottom: 6 }}>
                  Nome e cognome di chi firma
                </label>
                <NomeInput id="firma-nome" value={nome} onChange={setNome} />

                <div style={{ position: "relative", border: `1px solid ${COLOR.borderInput}`, borderRadius: 14, background: "#fff", height: 200, overflow: "hidden" }}>
                  <PadFirma ref={padRef} onCambio={setHaFirmaDisegnata} />
                  {!haFirmaDisegnata && (
                    <>
                      <div style={{ position: "absolute", left: 40, right: 40, bottom: 58, height: 1, background: COLOR.borderInner, pointerEvents: "none" }} />
                      <div style={{ position: "absolute", left: 40, bottom: 30, fontSize: 12.5, color: COLOR.padPlaceholder, pointerEvents: "none" }}>Firma sopra la riga</div>
                    </>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 26, margin: "8px 0 18px" }}>
                  {haFirmaDisegnata ? (
                    <span style={{ fontSize: 12.5, fontWeight: 500, color: COLOR.positiveText }}>Firma acquisita</span>
                  ) : (
                    <span style={{ fontSize: 12.5, color: COLOR.labelUpper }}>Usa il dito, la penna o il mouse</span>
                  )}
                  <button
                    type="button"
                    onClick={() => padRef.current?.pulisci()}
                    style={{ border: 0, background: "none", padding: 0, fontSize: 12.5, fontWeight: 600, color: COLOR.accent, cursor: "pointer" }}
                    data-testid="firma-cancella-btn"
                  >
                    Cancella
                  </button>
                </div>

                <div style={{ display: "flex", gap: 12 }}>
                  <BottoneSecondario onClick={() => setStep(2)} testId="firma-step3-indietro">Indietro</BottoneSecondario>
                  <BottonePrimario onClick={inviaFirmaTouch} disabled={!(haFirmaDisegnata && nome.trim()) || inviandoFirma} testId="firma-invia-btn">
                    {inviandoFirma ? <Loader2 className="animate-spin" style={{ width: 16, height: 16, display: "inline" }} /> : "Firma e invia"}
                  </BottonePrimario>
                </div>

                {!mostraUploadCarta ? (
                  <div style={{ marginTop: 16, fontSize: 12.5, color: COLOR.textSecondary }}>
                    Hai già firmato su carta?{" "}
                    <a href="#carta" onClick={(e) => { e.preventDefault(); setMostraUploadCarta(true); }} data-testid="firma-carta-toggle">
                      Carica il contratto firmato
                    </a>
                  </div>
                ) : (
                  <div style={{ marginTop: 16, padding: "16px 18px", background: "#fff", border: `1px solid ${COLOR.borderInner}`, borderRadius: 14 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLOR.textPrimary, marginBottom: 4 }}>Carica il contratto firmato</div>
                    <p style={{ margin: "0 0 12px", fontSize: 12.5, lineHeight: 1.6, color: COLOR.textSecondary }}>
                      Stampa il contratto, firmalo su ogni pagina richiesta, poi carica qui la scansione o la foto (un unico file: PDF, JPG o PNG, fino a 15MB).
                      Il nome inserito sopra viene usato anche per questo invio.
                    </p>
                    <input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png"
                      capture="environment"
                      onChange={(e) => onSelezionaFile(e.target.files?.[0])}
                      style={{ fontSize: 13 }}
                      data-testid="firma-upload-input"
                    />
                    {fileCaricato && (
                      <div style={{ fontSize: 12, color: COLOR.textSecondary, marginTop: 6 }}>
                        {fileCaricato.name} — {formatNumber((fileCaricato.size / 1024 / 1024).toFixed(1))} MB
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                      <BottoneSecondario onClick={() => { setMostraUploadCarta(false); setFileCaricato(null); }} testId="firma-carta-annulla">Annulla</BottoneSecondario>
                      <BottonePrimario onClick={inviaUpload} disabled={!fileCaricato || !nome.trim() || caricandoUpload} testId="firma-upload-btn">
                        {caricandoUpload ? <Loader2 className="animate-spin" style={{ width: 16, height: 16, display: "inline" }} /> : <>
                          <Upload style={{ width: 14, height: 14, display: "inline", marginRight: 6, verticalAlign: -2 }} />
                          Carica il contratto firmato
                        </>}
                      </BottonePrimario>
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>

          {/* Footer legale, persistente */}
          <div style={{ padding: "18px 24px 22px", textAlign: "center", fontSize: 11.5, lineHeight: 1.7, color: COLOR.headerTextSecondary, borderTop: `1px solid ${COLOR.borderInner}`, background: "#fff" }}>
            Diecixcento s.r.l.s. — Via Livorno 63, Ladispoli (RM) — P.IVA 17728001003 — PEC diecixcentosrls@pec.it
          </div>
        </div>
      </div>
    </div>
  );
}

function RigaConsenso({ checked, onChange, titolo, testo, testId }) {
  const [hover, setHover] = useState(false);
  return (
    <label
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", gap: 14, alignItems: "flex-start", padding: "18px 20px",
        border: `1px solid ${hover ? COLOR.borderHover : COLOR.borderInner}`, borderRadius: 14,
        background: "#fff", cursor: "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 19, height: 19, margin: "2px 0 0", accentColor: COLOR.accent, flex: "none", cursor: "pointer" }}
        data-testid={testId}
      />
      <span>
        <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: COLOR.textPrimary, marginBottom: 6 }}>{titolo}</span>
        <span style={{ display: "block", fontSize: 12.5, lineHeight: 1.65, color: COLOR.textSecondary }}>{testo}</span>
      </span>
    </label>
  );
}

function NomeInput({ id, value, onChange }) {
  const [focus, setFocus] = useState(false);
  return (
    <input
      id={id}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
      style={{
        width: "100%", boxSizing: "border-box", padding: "13px 14px",
        border: `1px solid ${focus ? COLOR.accent : COLOR.borderInput}`, borderRadius: 10,
        fontSize: 15, color: COLOR.textPrimary, background: "#fff", outline: "none", marginBottom: 16,
        boxShadow: focus ? `0 0 0 3px ${COLOR.focusRing}` : "none",
      }}
      data-testid="firma-nome-input"
    />
  );
}
